import React, { useEffect, useCallback, useMemo, memo } from "react";
import {
  Box,
  TextField,
  Select,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Paper,
  Grid,
  SelectChangeEvent,
  InputLabel,
  FormControl,
  Autocomplete,
} from "@mui/material";
import axios from "axios";
import { toast } from "react-toastify";

interface Medication {
  group: string;
  name: string;
}

interface FormData {
  specimen: string;
  ph: string;
  specificGravity: string;
  pusCells: string;
  epithelialCells: string;
  noOfPhrases: number;
  growthPattern: string;
  culturePhrases: string;
  colonyCount: string;
  gramStain: string;
  cultureGrowth: string;
  sensitive_1: { [key: number]: string };
  sensitive_2: { [key: number]: string };
  sensitive_3: { [key: number]: string };
}

interface CultureTestFormProps {
  cultureMedicines?: any[];
  cultureTestResults?: any[];
  setCultureTestResults?: (results: any[]) => void;
  chemicalExamination?: any;
  setChemicalExamination?: (examination: any) => void;
  testName?: string;
  index: number;
}

const CultureTestForm: React.FC<CultureTestFormProps> = memo(
  ({
    testName,
    cultureMedicines,
    cultureTestResults,
    setCultureTestResults,
    chemicalExamination,
    setChemicalExamination,
    index,
  }) => {
    const [formData, setFormData] = React.useState<FormData>({
      specimen: "",
      ph: "",
      specificGravity: "",
      pusCells: "",
      epithelialCells: "",
      noOfPhrases: 0,
      growthPattern: "",
      culturePhrases: "",
      colonyCount: "",
      gramStain: "",
      cultureGrowth: "",
      sensitive_1: {},
      sensitive_2: {},
      sensitive_3: {},
    });
    // Memoize the chemical examination data to prevent unnecessary updates
    const chemicalExaminationData = useMemo(
      () => ({
        specimen: formData.specimen,
        ph: formData.ph,
        specificGravity: formData.specificGravity,
        pusCells: formData.pusCells,
        epithelialCells: formData.epithelialCells,
        colonyCount: formData.colonyCount,
        noOfPhrases: formData.noOfPhrases,
        cultureGrowth: formData.cultureGrowth,
      }),
      [
        formData.specimen,
        formData.ph,
        formData.specificGravity,
        formData.pusCells,
        formData.epithelialCells,
        formData.colonyCount,
        formData.noOfPhrases,
        formData.cultureGrowth,
      ]
    );

    // Memoize culture test results to prevent unnecessary recalculation
    const cultureTestResultsData = useMemo(() => {
      if (!cultureMedicines || !setCultureTestResults) return [];
      return cultureMedicines.map((med, index) => ({
        medicineName: med.medicineName,
        medicineGroup: med.medicineGroup,
        sortOrder: index + 1,
        sensitive_1: formData.sensitive_1[index] || "",
        sensitive_2: formData.sensitive_2[index] || "",
        sensitive_3: formData.sensitive_3[index] || "",
      }));
    }, [
      cultureMedicines,
      formData.sensitive_1,
      formData.sensitive_2,
      formData.sensitive_3,
    ]);

    // Update parent data only when values actually change
    useEffect(() => {
      setChemicalExamination?.(chemicalExaminationData);
    }, [chemicalExaminationData, setChemicalExamination]);

    useEffect(() => {
      if (setCultureTestResults && cultureTestResultsData.length > 0) {
        setCultureTestResults(cultureTestResultsData);
      }
    }, [cultureTestResultsData, setCultureTestResults]);
    useEffect(() => {
      if (chemicalExamination) {
        setFormData(prev => ({
          ...prev,
          specimen: chemicalExamination.specimen || "",
          ph: chemicalExamination.ph || "",
          specificGravity: chemicalExamination.specificGravity || "",
          pusCells: chemicalExamination.pusCells || "",
          epithelialCells: chemicalExamination.epithelialCells || "",
          noOfPhrases: chemicalExamination.noOfPhrases || 0,
          growthPattern: chemicalExamination.growthPattern || "",
          culturePhrases: chemicalExamination.culturePhrases || "",
          colonyCount: chemicalExamination.colonyCount || "",
          gramStain: chemicalExamination.gramStain || "",
          cultureGrowth: chemicalExamination.cultureGrowth || "",
        }));
      }
      if (cultureTestResults && cultureTestResults.length > 0) {
        const sensitive_1: { [key: number]: string } = {};
        const sensitive_2: { [key: number]: string } = {};
        const sensitive_3: { [key: number]: string } = {};
        cultureTestResults.forEach((result, index) => {
          sensitive_1[index] = result.sensitive_1 || "";
          sensitive_2[index] = result.sensitive_2 || "";
          sensitive_3[index] = result.sensitive_3 || "";
        });
        setFormData(prev => ({
          ...prev,
          sensitive_1,
          sensitive_2,
          sensitive_3,
        }));
      }
    }, []);

    interface Specimen {
      name: string;
      [key: string]: any;
    }
    const [specimens, setSpecimens] = React.useState<Specimen[]>([]);
    const BASE = process.env.REACT_APP_LAB_URL;

    // Memoize the fetch function to prevent recreation on every render
    const fetchSpecimens = useCallback(async () => {
      try {
        const res = await axios.get(`${BASE}/specimen`, {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        });
        setSpecimens(res.data?.data || []);
      } catch (error: any) {
        console.error("Error fetching specimens", error);
        toast.error(error?.response.data.message || "Error fetching specimens");

      }
    }, [BASE]);

    // Only fetch specimens once when component mounts
    useEffect(() => {
      fetchSpecimens();
    }, [fetchSpecimens]);

    const handleTextChange = useCallback(
      (e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
      },
      []
    );

    const handleSelectChange = useCallback((e: SelectChangeEvent<string>) => {
      const { name, value } = e.target;
      setFormData(prev => ({ ...prev, [name]: value }));
    }, []);

    // Memoize dropdown enablement to prevent recalculation
    const dropdownStates = useMemo(
      () => ({
        // isSensitiveIEnabled: formData.noOfPhrases >= 0,
        isSensitiveIEnabled: formData.noOfPhrases >= 1,
        isSensitiveIIEnabled: formData.noOfPhrases >= 2,
        isSensitiveIIIEnabled: formData.noOfPhrases >= 3,
      }),
      [formData.noOfPhrases]
    );

    const { isSensitiveIEnabled, isSensitiveIIEnabled, isSensitiveIIIEnabled } =
      dropdownStates;

    // Specimen options
    return (
      <Paper>
        <Box mb={2}>Culture Test Results</Box>

        {/* Row 1: Specimen only */}
        <Grid container spacing={2}>
          <Grid item xs={12} sm={3}>
            <Autocomplete
              className="custom-input"
              options={specimens || []}
              getOptionLabel={(option: any) => option.name || ""}
              value={
                specimens?.find((s: any) => s.name === formData.specimen) ||
                null
              }
              onChange={(_, newValue) => {
                setFormData(prev => ({
                  ...prev,
                  specimen: newValue ? newValue.name : "",
                }));
              }}
              size="small"
              renderInput={params => (
                <TextField
                  {...params}
                  label="Select Specimen"
                  variant="outlined"
                  fullWidth
                />
              )}
            />
          </Grid>
        </Grid>

        {/* Row 2: Chemical and Microscopic Examinations */}
        {testName === "Culture Urine" && (
          <Grid container spacing={2} mt={1}>
            {/* Chemical Examination Column */}
            <Grid item xs={12} sm={6}>
              <Box mb={1} sx={{ fontWeight: "bold", fontSize: "14px" }}>
                Chemical Examination
              </Box>
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <TextField
                    size="small"
                    className="custom-input"
                    label="PH"
                    name="ph"
                    value={formData.ph}
                    onChange={handleTextChange}
                    fullWidth
                  />
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    size="small"
                    className="custom-input"
                    label="Specific Gravity"
                    name="specificGravity"
                    value={formData.specificGravity}
                    onChange={handleTextChange}
                    fullWidth
                  />
                </Grid>
              </Grid>
            </Grid>

            {/* Microscopic Examination Column */}
            <Grid item xs={12} sm={6}>
              <Box mb={1} sx={{ fontWeight: "bold", fontSize: "14px" }}>
                Microscopic Examination
              </Box>
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <TextField
                    size="small"
                    className="custom-input"
                    label="Pus Cells"
                    name="pusCells"
                    value={formData.pusCells}
                    onChange={handleTextChange}
                    fullWidth
                  />
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    size="small"
                    className="custom-input"
                    label="Epithelial Cells"
                    name="epithelialCells"
                    value={formData.epithelialCells}
                    onChange={handleTextChange}
                    fullWidth
                  />
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        )}

        {/* Row 3: Remaining fields */}
        <Grid container spacing={2} mt={1}>
          <Grid item xs={12} sm={4}>
            <FormControl fullWidth>
              <InputLabel
                sx={{ backgroundColor: "white" }}
                id="noOfPhrases-label"
              >
                No. of Phrases
              </InputLabel>
              <Select
                className="custom-input"
                labelId="noOfPhrases-label"
                id="noOfPhrases"
                name="noOfPhrases"
                value={formData.noOfPhrases.toString()}
                onChange={e =>
                  setFormData(prev => ({
                    ...prev,
                    noOfPhrases: Number(e.target.value),
                  }))
                }
              >
                {[0,1, 2, 3].map(num => (
                  <MenuItem key={num} value={num}>
                    {num}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField
              size="small"
              className="custom-input"
              label="Colony Count"
              name="colonyCount"
              value={formData.colonyCount}
              onChange={handleTextChange}
              fullWidth
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField
              size="small"
              className="custom-input"
              label="Culture Growth"
              name="cultureGrowth"
              value={formData.cultureGrowth}
              onChange={handleTextChange}
              fullWidth
              sx={{ mb: 2 }}
            />
          </Grid>
        </Grid>

        {/* Table */}
        <Box mt={2} mb={1}>
          Medicines
        </Box>
        <Box
          sx={{
            height: "350px",
            overflow: "auto",
            border: "1px solid #ddd",
          }}
        >
          <Table
            size="small"
            sx={{
              "& th, & td": {
                padding: "2px 6px",
                fontSize: "12px",
                lineHeight: 1.2,
              },
            }}
          >
            <TableHead
              sx={{
                position: "sticky",
                top: 0,
                zIndex: 1,
                backgroundColor: "#fff",
              }}
            >
              <TableRow sx={{ background: "#f7f7f7" }}>
                <TableCell sx={{ fontWeight: "bold", width: "15%" }}>
                  Group name
                </TableCell>
                <TableCell sx={{ fontWeight: "bold", width: "25%" }}>
                  Medicine (Antibiotics)
                </TableCell>
                <TableCell sx={{ fontWeight: "bold", width: "20%" }}>
                  Sensitive-I
                </TableCell>
                <TableCell sx={{ fontWeight: "bold", width: "20%" }}>
                  Sensitive-II
                </TableCell>
                <TableCell sx={{ fontWeight: "bold", width: "20%" }}>
                  Sensitive-III
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {cultureMedicines?.map((med, index) => (
                <TableRow key={index}>
                  <TableCell>{med.medicineGroup}</TableCell>
                  <TableCell>{med.medicineName}</TableCell>
                  <TableCell>
                    <Select
                      size="small"
                      value={formData.sensitive_1[index] || ""}
                      onChange={e =>
                        setFormData(prev => ({
                          ...prev,
                          sensitive_1: {
                            ...prev.sensitive_1,
                            [index]: e.target.value,
                          },
                        }))
                      }
                      error={index === 0 && !formData.sensitive_1[index]}
                      disabled={!isSensitiveIEnabled}
                      sx={{
                        fontSize: "12px",
                        height: "28px",
                        width: "100%",
                        backgroundColor: !isSensitiveIEnabled
                          ? "#e0e0e0"
                          : "inherit",
                      }}
                    >
                      <MenuItem value="">
                        <em>None</em>
                      </MenuItem>
                      <MenuItem value="I">I (Intermediate)</MenuItem>
                      <MenuItem value="S">S (Sensitive)</MenuItem>
                      <MenuItem value="R">R (Resistant)</MenuItem>
                    </Select>
                  </TableCell>
                  <TableCell>
                    <Select
                      size="small"
                      value={formData.sensitive_2[index] || ""}
                      onChange={e =>
                        setFormData(prev => ({
                          ...prev,
                          sensitive_2: {
                            ...prev.sensitive_2,
                            [index]: e.target.value,
                          },
                        }))
                      }
                      disabled={!isSensitiveIIEnabled}
                      sx={{
                        fontSize: "12px",
                        height: "28px",
                        width: "100%",
                        backgroundColor: !isSensitiveIIEnabled
                          ? "#e0e0e0"
                          : "inherit",
                      }}
                    >
                      <MenuItem value="">
                        <em>None</em>
                      </MenuItem>
                      <MenuItem value="I">I (Intermediate)</MenuItem>
                      <MenuItem value="S">S (Sensitive)</MenuItem>
                      <MenuItem value="R">R (Resistant)</MenuItem>
                    </Select>
                  </TableCell>
                  <TableCell>
                    <Select
                      size="small"
                      value={formData.sensitive_3[index] || ""}
                      onChange={e =>
                        setFormData(prev => ({
                          ...prev,
                          sensitive_3: {
                            ...prev.sensitive_3,
                            [index]: e.target.value,
                          },
                        }))
                      }
                      disabled={!isSensitiveIIIEnabled}
                      sx={{
                        fontSize: "12px",
                        height: "28px",
                        width: "100%",
                        backgroundColor: !isSensitiveIIIEnabled
                          ? "#e0e0e0"
                          : "inherit",
                      }}
                    >
                      <MenuItem value="">
                        <em>None</em>
                      </MenuItem>
                      <MenuItem value="I">I (Intermediate)</MenuItem>
                      <MenuItem value="S">S (Sensitive)</MenuItem>
                      <MenuItem value="R">R (Resistant)</MenuItem>
                    </Select>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Box>
      </Paper>
    );
  }
);

export default CultureTestForm;
